import { useEffect } from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils.js';
import { useNotificationStore } from '@/stores/notificationStore.js';
import { useNavigate } from 'react-router-dom';
import { useServices } from '@/contexts/ServicesContext.js';
import { useChatTabsStore } from '@/stores/chatTabsStore.js';

const INTENT_STYLES: Record<string, string> = {
  info: 'border-blue-400/60 bg-blue-50 dark:bg-blue-950/40',
  warning: 'border-orange-400/60 bg-orange-50 dark:bg-orange-950/40',
  success: 'border-green-400/60 bg-green-50 dark:bg-green-950/30',
  danger: 'border-red-400/60 bg-red-50 dark:bg-red-950/40',
};

export function Toaster() {
  const toasts = useNotificationStore((state) => state.toasts);
  const dismissToast = useNotificationStore((state) => state.dismissToast);
  const navigate = useNavigate();
  const { sessionService } = useServices();
  const { addTab, setActiveTab, getTab } = useChatTabsStore();

  // Auto-dismiss after 6s
  useEffect(() => {
    const timers = toasts.map((toast) =>
      setTimeout(() => dismissToast(toast.id), 6000)
    );
    return () => {
      timers.forEach(clearTimeout);
    };
  }, [toasts, dismissToast]);

  if (toasts.length === 0) {
    return null;
  }

  return (
    <div className="fixed top-4 right-4 z-50 space-y-2 w-[320px]">
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className={cn(
            'rounded-lg border shadow-sm px-4 py-3',
            'backdrop-blur bg-background/90',
            INTENT_STYLES[toast.intent || 'info']
          )}
        >
          <div className="flex items-start gap-3">
            <div className="flex-1">
              <p className="text-sm font-semibold text-foreground">{toast.title}</p>
              {toast.description && (
                <p className="text-xs text-muted-foreground mt-0.5">{toast.description}</p>
              )}
            </div>
            <button
              className="text-muted-foreground hover:text-foreground"
              onClick={() => dismissToast(toast.id)}
              aria-label="Dismiss"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {toast.targetSessionId && (
            <button
              className="mt-2 text-xs text-primary hover:underline"
              onClick={async () => {
                try {
                  console.log('[Toaster] Opening/resuming session via toast:', toast.targetSessionId);

                  // Check if a tab already exists for this session
                  const tabId = `session-${toast.targetSessionId}`;
                  const existingTab = getTab(tabId);

                  if (existingTab) {
                    // Tab exists - just activate it
                    console.log('[Toaster] Tab already exists, activating:', tabId);
                    setActiveTab(tabId);
                  } else {
                    // Tab doesn't exist - create a new one
                    console.log('[Toaster] Creating new tab for session:', toast.targetSessionId);
                    addTab({
                      id: tabId,
                      title: 'Chat', // Will be updated when session loads
                      sessionId: toast.targetSessionId ?? null,
                      isPinned: false,
                    });
                  }

                  // Switch to the session and navigate
                  await sessionService.switchSession(toast.targetSessionId!);
                  navigate(`/chat/${toast.targetSessionId}`);
                  dismissToast(toast.id);
                } catch (error) {
                  console.error('[Toaster] Failed to switch session:', error);
                  dismissToast(toast.id);
                }
              }}
            >
              Open chat
            </button>
          )}
        </div>
      ))}
    </div>
  );
}
